import React, { useEffect, useState } from 'react';

import { observer, inject } from 'mobx-react';
import { CaseCard as UICaseCard } from '@shopee/chatbot-ui';
import { GeneralButton } from '@/interfaces';
import { getContent } from '@/utils/tools';
import { ButtonAction, ButtonActionV1, ButtonTypeV1, getButtonActionV1, getButtonDataV1, getButtonTypeV1 } from '@/utils/chatuiAdapters';
import { ButtonProps } from '@shopee/chatbot-ui/lib/components/Button';
import { InitStoreType } from '@/types/store/init';
import { getChildInject } from '@/utils/instantLoaded/tools';
import { INPUT_TYPE } from '@/utils/constants';
import { TRACKING_SECTION, TRACKING_TARGET, TRACKING_TYPE } from '@/utils/tracking/constants';
import { updateDialogueInfo } from '@/utils/sessionUtils';
import { AgentStore } from '@/stores/agent';
import { ButtonEvents } from '@shopee/chatbot-ui/lib/types';
import { InhouseCaseEntity, InhouseCaseStatus } from '@/types/common';
import { formatCaseDesc } from '@/utils/channelTools/formatCaseDesc';

import dateLib from '@/utils/dateLib/moment';

export type CaseCardProps = {
  caseTrackerData: InhouseCaseEntity & {
    uriKey: string;
    uriExtra: {
      case_id: string;
    }
  };
  buttons: GeneralButton[];
  liveButtonInfo: {
    userType: number;
    enquiryType: number;
  }
} & {
  initStore: InitStoreType;
  agentStore: AgentStore;
  dialogueId: string;
  dialogueType: string;
  listPos: number;
  isDisabled: boolean;
  isHistory: boolean;
  historySessionId: string;
  historyDialogueId: string;
}

const CASE_STATUS_MAP_COLOR = {
  [InhouseCaseStatus.Received]: '#4080EE',
  [InhouseCaseStatus.InProgress]: '#EDA500',
  [InhouseCaseStatus.ActionRequired]: '#EE2C4A',
  [InhouseCaseStatus.Solved]: '#30B566',
  [InhouseCaseStatus.Completed]: '#00000042',
};

const CaseCard: React.FC<CaseCardProps> = ({
  caseTrackerData,
  buttons,
  dialogueId,
  dialogueType,
  listPos,
  isDisabled,
  initStore,
  agentStore,
  liveButtonInfo,
  isHistory,
  historySessionId,
  historyDialogueId,
}) => {
  const [formattedCaseDesc, setFormattedCaseDesc] = useState('');
  const { id: caseId, status, title, case_desc, order_pic_url, status_desc, update_time, create_method, uriKey, uriExtra, } = caseTrackerData;
  const { loadingInAgent, inAgent } = agentStore;

  const getCaseCardButtonData = (button: GeneralButton, isButton: boolean = true) => {
    if (!button.button_type) {
      return {};
    }
    // 退出进线时 会需要带一个参数 让 message 不会落库
    // 当 message text 为空值是 就携带
    const otherParams = isButton ? {} : { is_bypass: true };

    const nextParams = {
      ...otherParams,
      session_id: initStore?.sessionId,
      dialogue_id: dialogueId,
      input_type: INPUT_TYPE.BUTTON,
      message_text: isButton ? getContent(button) : '',
      message_extra: {
        case_tracker_params: {
          case_id: caseId,
          last_status: status,
          button_type: isButton ? button.button_type : '',
          create_method,
        },
      },
    };

    const getDataWithButtonType = {
      [ButtonActionV1.NUDGE_CASE]: () => ({
        nextParams,
        callback: () => {
          updateDialogueInfo({ isDisabled: true }, listPos);
        }
      }),
      [ButtonActionV1.REDIRECTION]: () => ({
        ...getButtonDataV1(button),
        buttonInfo: {
          callback_action: {
            action_type: ButtonAction.JumpNode,
            nextParams,
            successCb: (data: any) => {
              const newCaseTrackerData = data?.replies?.[0]?.message_extra?.case_tracker_item || {};
              if (newCaseTrackerData) {
                updateDialogueInfo(
                  {
                    caseTrackerData: {
                      ...caseTrackerData,
                      ...newCaseTrackerData,
                    },
                  },
                  listPos,
                );
              }
            },
          }
        }
      }),
      [ButtonActionV1.LIVE]: () => ({
        info: {
          dialogueId,
          dialogueType,
          listPos,
        },
        live_agent: {
          isChatbotMsgBeforeLiveAgent: true,
          inhouse_queue: {
            user_type_id: liveButtonInfo.userType,
            enquiry_type_id: liveButtonInfo.enquiryType,
          },
        },
      }),
    };

    return getDataWithButtonType[button.button_type as keyof typeof getDataWithButtonType]?.() || {};
  };

  const formatButtons = () => {
    return buttons.map(btn => ({
      text: getContent(btn),
      type: getButtonTypeV1(btn.style as ButtonTypeV1),
      disabled: isDisabled || isHistory,
      action: {
        func: getButtonActionV1(btn.button_type as ButtonActionV1),
        ...getCaseCardButtonData(btn),
      },
      trackingData: getButtonTrackingData(btn),
    }));
  };

  const getButtonTrackingData = (button: GeneralButton) => {
    return {
      trackingsection: TRACKING_SECTION[TRACKING_TYPE.CASE_TRACKER_BUTTON],
      trackingtarget: TRACKING_TARGET[TRACKING_TYPE.CASE_TRACKER_BUTTON],
      trackingdataBasic: {
        chat_session_id: isHistory ? historySessionId : (initStore.sessionId || ''),
        chat_dialogue_id: isHistory ? historyDialogueId : dialogueId,
        button_type: button.button_type,
        button_id: button.button_id,
        button_text: getContent(button),
        case_status: caseTrackerData?.status?.toString?.(),
        is_to_live_agent: button.button_type === ButtonActionV1.LIVE ? 1 : 0,
      },
    };
  };

  const getCaseTrackingData = () => {
    return {
      trackingsection: TRACKING_SECTION[TRACKING_TYPE.CASE_TRACKER_CARD],
      trackingtarget: TRACKING_TARGET[TRACKING_TYPE.CASE_TRACKER_CARD],
      trackingdataBasic: {
        chat_session_id: isHistory ? historySessionId : (initStore.sessionId || ''),
        chat_dialogue_id: isHistory ? historyDialogueId : dialogueId,
        case_status: caseTrackerData?.status?.toString?.(),
      },
    };
  };

  useEffect(() => {
    // 只要进线，就禁用按钮
    if ((loadingInAgent || inAgent) && !isDisabled) {
      updateDialogueInfo({ isDisabled: true }, listPos);
    }
  }, [loadingInAgent, inAgent, isDisabled, listPos]);

  useEffect(() => {
    formatCaseDesc(caseTrackerData)
      .then(value => {
        setFormattedCaseDesc(value);
      });
  }, [case_desc]);

  return (
    <UICaseCard
      title={title}
      desc={formattedCaseDesc}
      image={order_pic_url}
      statusText={status_desc}
      statusColor={CASE_STATUS_MAP_COLOR[status]}
      date={dateLib.format(update_time * 1000, 'MMM DD, YYYY HH:ii')}
      buttons={{ buttons: formatButtons() as ButtonProps[] }}
      cardAction={isHistory ? undefined : {
        func: ButtonEvents.Redirection,
        ...getCaseCardButtonData({
          button_type: 'btn_redirection',
          uri_key: uriKey,
          uri_extra: uriExtra,
        }, false),
      }}
      trackingData={getCaseTrackingData()}
    />
  );
};

export default inject(getChildInject('initStore', 'agentStore'))(observer(CaseCard));
